import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Inicio", to: "/" },
  { label: "Persianas", to: "/persianas", hasMegaMenu: true },
  { label: "Toldos", to: "/toldos" },
  { label: "Mosquiteras", to: "/mosquiteras" },
  { label: "Zonas", to: "/zonas" },
  { label: "Contacto", to: "/contacto" },
];

const zoneColumns = [
  {
    title: "Costa del Sol Occidental",
    image: "https://images.unsplash.com/photo-1596120236172-23199970d6cc?w=400&q=80&auto=format&fit=crop",
    links: [
      { label: "Málaga", to: "/persianas/malaga" },
      { label: "Torremolinos", to: "/persianas/torremolinos" },
      { label: "Benalmádena", to: "/persianas/benalmadena" },
      { label: "Fuengirola", to: "/persianas/fuengirola" },
      { label: "Mijas", to: "/persianas/mijas" },
      { label: "Marbella", to: "/persianas/marbella" },
      { label: "San Pedro de Alcántara", to: "/persianas/san-pedro-de-alcantara" },
      { label: "Benahavís", to: "/persianas/benahavis" },
      { label: "Estepona", to: "/persianas/estepona" },
    ],
  },
  {
    title: "Axarquía / Costa Oriental",
    image: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=400&q=80&auto=format&fit=crop",
    links: [
      { label: "Rincón de la Victoria", to: "/persianas/rincon-de-la-victoria" },
      { label: "Vélez-Málaga", to: "/persianas/velez-malaga" },
      { label: "Nerja", to: "/persianas/nerja" },
    ],
  },
  {
    title: "Guadalhorce",
    image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&q=80&auto=format&fit=crop",
    links: [
      { label: "Alhaurín de la Torre", to: "/persianas/alhaurin-de-la-torre" },
      { label: "Alhaurín el Grande", to: "/persianas/alhaurin-el-grande" },
      { label: "Coín", to: "/persianas/coin" },
      { label: "Cártama", to: "/persianas/cartama" },
      { label: "Álora", to: "/persianas/alora" },
    ],
  },
  {
    title: "Antequera y Norte",
    image: "https://images.unsplash.com/photo-1507608869274-d3177c8bb4c7?w=400&q=80&auto=format&fit=crop",
    links: [
      { label: "Antequera", to: "/persianas/antequera" },
    ],
  },
  {
    title: "Serranía",
    image: "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=400&q=80&auto=format&fit=crop",
    links: [
      { label: "Ronda", to: "/persianas/ronda" },
    ],
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileAccordionOpen, setMobileAccordionOpen] = useState(false);
  const [hoveredPreview, setHoveredPreview] = useState<{ city: string; zoneImage: string; zoneTitle: string } | null>(null);
  const megaPanelRef = useRef<HTMLDivElement>(null);
  const megaButtonRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  const currentPath = location.pathname;

  const isHome = currentPath === "/" || currentPath === "/index.html";

  // Scroll detection — transparent on homepage until 50px, solid on other pages
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > (isHome ? 50 : 0));
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Hover handlers with delay to prevent gap flickering
  const openMega = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setMegaOpen(true);
  }, []);
  const closeMega = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setMegaOpen(false);
      setHoveredPreview(null);
    }, 200);
  }, []);

  // Cierra mega menú al hacer click fuera
  useEffect(() => {
    if (!megaOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insidePanel = megaPanelRef.current?.contains(target);
      const insideButton = megaButtonRef.current?.contains(target);
      if (!insidePanel && !insideButton) {
        setMegaOpen(false);
        setHoveredPreview(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [megaOpen]);

  // Cierra todo al cambiar de ruta
  useEffect(() => {
    setMegaOpen(false);
    setMobileOpen(false);
    setMobileAccordionOpen(false);
    setHoveredPreview(null);
  }, [location.pathname]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCityHover = useCallback((city: string, zoneImage: string, zoneTitle: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setHoveredPreview({ city, zoneImage, zoneTitle });
  }, []);

  return (
    <>
      {/* Skip to content link */}
      <a href="#main-content" className="skip-link">
        Saltar al contenido principal
      </a>

      <header
        className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-200 ease-out ${
          isHome && !scrolled
            ? "bg-transparent"
            : "bg-white/80 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.06)]"
        }`}
        role="banner"
      >
        {/* Top bar: logo, nav, phone */}
        <div
          className={`max-w-container mx-auto px-4 md:px-6 flex items-center justify-between transition-all duration-200 ${
            scrolled ? "h-14 md:h-16" : "h-16 md:h-[72px]"
          }`}
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0 touch-target"
            aria-label="Persianas Toldos Málaga — Inicio"
          >
            <span
              className={`font-heading font-bold text-primary whitespace-nowrap transition-all duration-300 ${
                scrolled ? "text-lg md:text-xl" : "text-xl md:text-2xl"
              }`}
            >
              Persianas Toldos Málaga
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" role="navigation" aria-label="Navegación principal">
            {navLinks.map((link) => (
              <div key={link.to} className="relative">
                {link.hasMegaMenu ? (
                  <>
                    <div
                      className="relative flex items-center"
                      onMouseEnter={openMega}
                      onMouseLeave={closeMega}
                    >
                      <Link
                        to={link.to}
                        className={`relative font-body font-medium text-sm transition-colors whitespace-nowrap touch-target py-1 ${
                          currentPath === link.to || currentPath === link.to + "/" || currentPath.startsWith(link.to + "/")
                            ? "text-primary"
                            : "text-text-primary hover:text-primary"
                        }`}
                        aria-current={
                          currentPath === link.to || currentPath === link.to + "/"
                            ? "page"
                            : undefined
                        }
                        onClick={() => setMegaOpen(false)}
                      >
                        {link.label}
                        {(currentPath === link.to || currentPath === link.to + "/" || currentPath.startsWith(link.to + "/")) && (
                          <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" aria-hidden="true"></span>
                        )}
                      </Link>
                      <button
                        ref={megaButtonRef}
                        className={`ml-0.5 w-6 h-6 flex items-center justify-center rounded-md transition-colors ${
                          megaOpen ? "text-primary bg-primary/10" : "text-text-secondary hover:text-primary hover:bg-primary/5"
                        }`}
                        onClick={() => setMegaOpen((v) => !v)}
                        aria-expanded={megaOpen}
                        aria-haspopup="true"
                        aria-controls="mega-menu"
                        aria-label="Abrir menú de zonas"
                      >
                        <i
                          className={`ri-arrow-down-s-line transition-transform duration-200 ${megaOpen ? "rotate-180" : ""}`}
                          aria-hidden="true"
                        ></i>
                      </button>
                    </div>
                    {/* Invisible bridge */}
                    <div
                      className="absolute top-full left-1/2 -translate-x-1/2 w-24 h-3"
                      onMouseEnter={openMega}
                      onMouseLeave={closeMega}
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  <Link
                    to={link.to}
                    className={`relative font-body font-medium text-sm transition-colors whitespace-nowrap touch-target py-1 ${
                      currentPath === link.to || currentPath === link.to + "/"
                        ? "text-primary"
                        : "text-text-primary hover:text-primary"
                    }`}
                    aria-current={
                      currentPath === link.to || currentPath === link.to + "/"
                        ? "page"
                        : undefined
                    }
                  >
                    {link.label}
                    {(currentPath === link.to || currentPath === link.to + "/") && (
                      <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" aria-hidden="true"></span>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Phone CTA */}
          <div className="flex items-center gap-3">
            <a
              href="tel:+34951120770"
              className={`hidden md:inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-semibold rounded-pill transition-all duration-300 whitespace-nowrap touch-target ${
                scrolled ? "text-base px-5 py-2.5" : "text-[1.25rem] px-6 py-3"
              }`}
              aria-label="Llamar al 951 12 07 70"
            >
              <i className="ri-phone-line" aria-hidden="true"></i> 951 12 07 70
            </a>
            <a
              href="tel:+34951120770"
              className="md:hidden inline-flex items-center justify-center w-10 h-10 bg-primary hover:bg-primary-dark text-white rounded-full transition-colors touch-target"
              aria-label="Llamar al 951 12 07 70"
            >
              <i className="ri-phone-line" aria-hidden="true"></i>
            </a>

            {/* Mobile hamburger */}
            <button
              className="lg:hidden w-10 h-10 flex items-center justify-center text-text-primary touch-target"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menú"
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
            >
              <i className="ri-menu-line text-2xl" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {/* ===== MEGA-MENÚ DESKTOP ===== */}
        {megaOpen && (
          <div
            ref={megaPanelRef}
            id="mega-menu"
            className="hidden lg:block absolute top-full left-0 right-0 bg-[#FFFBF7] shadow-[0_24px_64px_rgba(249,115,22,0.10),0_4px_12px_rgba(0,0,0,0.04)] z-[9999] animate-mega-menu border-t-2 border-primary/20 rounded-b-2xl overflow-hidden"
            role="dialog"
            aria-label="Menú de zonas"
            onMouseEnter={openMega}
            onMouseLeave={closeMega}
          >
            <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <h3 className="font-heading font-bold text-lg text-primary">
                    Persianas por zona — Málaga
                  </h3>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary font-body font-semibold text-xs">
                    30+ zonas
                  </span>
                </div>
                <Link
                  to="/persianas"
                  onClick={() => { setMegaOpen(false); setHoveredPreview(null); }}
                  className="inline-flex items-center gap-1.5 font-body font-semibold text-sm text-primary hover:text-primary-dark transition-colors group"
                >
                  Ver todos los servicios <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" aria-hidden="true"></i>
                </Link>
              </div>

              {/* Columns + Preview */}
              <div className="flex gap-8">
                {/* Links grid */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8">
                  {zoneColumns.map((col) => (
                    <div
                      key={col.title}
                      className="lg:border-l lg:border-primary/10 lg:first:border-l-0 lg:pl-6 lg:first:pl-0"
                      onMouseEnter={() => {
                        if (!hoveredPreview) {
                          setHoveredPreview({ city: col.links[0]?.label || "", zoneImage: col.image, zoneTitle: col.title });
                        }
                      }}
                    >
                      <h4 className="font-heading font-bold text-sm text-primary mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary/60" aria-hidden="true"></span>
                        {col.title}
                      </h4>
                      <ul className="space-y-0.5" role="list">
                        {col.links.map((link) => (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              onClick={() => { setMegaOpen(false); setHoveredPreview(null); }}
                              onMouseEnter={() => handleCityHover(link.label, col.image, col.title)}
                              className="font-body font-medium text-sm text-text-secondary hover:text-primary hover:bg-primary/5 transition-all rounded-md px-2 -mx-2 py-1.5 block leading-relaxed"
                            >
                              {link.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Hover Preview Panel */}
                <div className="hidden lg:block w-[260px] shrink-0">
                  <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-gray-100">
                    {hoveredPreview ? (
                      <>
                        <img
                          src={hoveredPreview.zoneImage}
                          alt={`Vista de ${hoveredPreview.city}`}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                          loading="eager"
                          width={260}
                          height={195}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                          <p className="text-white/80 font-body text-xs uppercase tracking-wider mb-1">
                            {hoveredPreview.zoneTitle}
                          </p>
                          <p className="text-white font-heading font-bold text-lg leading-tight">
                            {hoveredPreview.city}
                          </p>
                          <span className="inline-flex items-center gap-1 mt-2 text-white/90 font-body text-xs">
                            Ver servicios <i className="ri-arrow-right-line" aria-hidden="true"></i>
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-text-secondary/40">
                        <i className="ri-map-pin-line text-4xl mb-2" aria-hidden="true"></i>
                        <p className="font-body text-sm">Pasa el ratón sobre una ciudad</p>
                      </div>
                    )}
                  </div>
                  <p className="mt-3 font-body text-xs text-text-secondary/60 text-center">
                    Servicio de reparación e instalación en toda la provincia
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ===== MOBILE OVERLAY ===== */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998] animate-fade-in lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />

          {/* Slide-in Panel */}
          <div
            id="mobile-menu"
            className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-[#FFFBF7] z-[9999] shadow-[-8px_0_32px_rgba(0,0,0,0.08)] flex flex-col lg:hidden animate-slide-in-right"
            role="navigation"
            aria-label="Navegación móvil"
          >
            {/* Mobile panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-primary/10 bg-white/50">
              <span className="font-heading font-bold text-lg text-primary">Menú</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors touch-target"
                aria-label="Cerrar menú"
              >
                <i className="ri-close-line text-xl" aria-hidden="true"></i>
              </button>
            </div>

            {/* Nav items */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.to} className="border-b border-primary/5 last:border-b-0">
                  {link.hasMegaMenu ? (
                    <div>
                      <button
                        onClick={() => setMobileAccordionOpen((v) => !v)}
                        className={`w-full flex items-center justify-between py-3.5 font-body font-medium text-base transition-colors touch-target ${
                          currentPath.startsWith(link.to)
                            ? "text-primary font-semibold"
                            : "text-text-primary"
                        }`}
                        aria-expanded={mobileAccordionOpen}
                      >
                        <Link
                          to={link.to}
                          onClick={() => setMobileOpen(false)}
                          className="flex-1 text-left"
                        >
                          {link.label}
                        </Link>
                        <span
                          className={`w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 text-primary transition-transform duration-300 ${
                            mobileAccordionOpen ? "rotate-180" : ""
                          }`}
                        >
                          <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
                        </span>
                      </button>
                      <div
                        className="overflow-hidden transition-all duration-300 ease-in-out"
                        style={{
                          maxHeight: mobileAccordionOpen ? "800px" : "0px",
                          opacity: mobileAccordionOpen ? 1 : 0,
                        }}
                      >
                        <div className="pb-4 space-y-4">
                          {zoneColumns.map((col) => (
                            <div key={col.title} className="pl-3 border-l-2 border-primary/20">
                              <h4 className="font-heading font-bold text-xs text-primary uppercase tracking-wide mb-2 flex items-center gap-2">
                                <span className="w-1 h-1 rounded-full bg-primary/50" aria-hidden="true"></span>
                                {col.title}
                              </h4>
                              <ul className="space-y-1" role="list">
                                {col.links.map((l) => (
                                  <li key={l.to}>
                                    <Link
                                      to={l.to}
                                      onClick={() => {
                                        setMobileOpen(false);
                                        setMobileAccordionOpen(false);
                                      }}
                                      className="font-body text-sm text-text-secondary hover:text-primary transition-colors block py-1.5 touch-target"
                                    >
                                      {l.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className={`block py-3.5 font-body font-medium text-base transition-colors touch-target ${
                        currentPath === link.to || currentPath === link.to + "/"
                          ? "text-primary font-semibold"
                          : "text-text-primary hover:text-primary"
                      }`}
                      aria-current={
                        currentPath === link.to || currentPath === link.to + "/"
                          ? "page"
                          : undefined
                      }
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Sticky CTA footer */}
            <div className="p-5 border-t border-primary/10 bg-white/60 space-y-3">
              <a
                href="tel:+34951120770"
                className="flex items-center justify-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-semibold text-base px-5 py-3.5 rounded-pill transition-colors touch-target"
                aria-label="Llamar al 951 12 07 70"
              >
                <i className="ri-phone-line" aria-hidden="true"></i> 951 12 07 70
              </a>
              <a
                href="https://wa.me/34627213347?text=Hola%2C%20me%20gustar%C3%ADa%20un%20presupuesto"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-whatsapp hover:bg-[#1ebe57] text-white font-body font-semibold text-base px-5 py-3.5 rounded-pill transition-colors touch-target"
                aria-label="Contactar por WhatsApp"
              >
                <i className="ri-whatsapp-line" aria-hidden="true"></i> WhatsApp
              </a>
            </div>
          </div>
        </>
      )}
    </>
  );
}